import { FC, useEffect, useMemo, useRef, useState } from "react";

import { getNavigationMap, NavigationRoute, parseWallClockDate, useOrganization } from "@asd/customer-portal-ui";
import { AppBar, Box, Card, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ConfigurationService } from "~/services";
import { setSelectedPPTConfiguration } from "~/store/configurationSlice";
import { env } from "~/utils/env";
import { BudgetData } from "../budget";
import { DashboardPPT } from "../dashboard_ppt";
import { DashboardPPTProvider } from "../dashboard_ppt/DashboardPPTProvider";
import Providers from "../Providers";
import { useDashboardStaffProvider } from "./DashboardStaffProvider";

const SelectedParticipantNavigationWrapper = (WrappedComponent: FC) => {
  const WrappedSelectedParticipantNavigation = (props: any) => {
    useDashboardStaffProvider();

    if (sessionStorage.getItem("pending_participant_id")) {
      return (
        <Box
          position="absolute"
          sx={(theme) => ({
            inset: 0,
            width: "100%",
            height: "100%",
            alignContent: "center",
            textAlign: "center",
            background: theme.palette.background.paper,
            zIndex: theme.zIndex.drawer - 1,
          })}
        >
          <CircularProgress size={150} />
          <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>Loading...</Typography>
        </Box>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return WrappedSelectedParticipantNavigation;
};

const SelectedParticipantNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    configuration: {
      ppt: { showCareProviders: showCareProvidersForPPT },
    },
  } = useOrganization();

  const { selectedParticipant } = useDashboardStaffProvider();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const configurationPromise = useRef<Promise<void> | null>(null);
  const configurationAbortController = useRef<AbortController>(new AbortController());

  const navigationMap = useMemo(
    () =>
      getNavigationMap({
        roles: [],
        groups: [],
        isSelectedPPT: true,
        showCareProvidersForPPT,
        withProviderRegistry: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
        withProviderDirectory: env.REACT_APP_WITH_PROVIDER_DIRECTORY !== "false",
        withMessages: env.REACT_APP_WITH_MESSAGES !== "false",
        withReports: env.REACT_APP_WITH_REPORTS !== "false",
        getParticipantLabel: () =>
          selectedParticipant ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}` : "Participant/Member",
        isParticipantSelected: () => !!selectedParticipant,
      }),
    [showCareProvidersForPPT, selectedParticipant],
  );

  useEffect(() => {
    const thisPromise = ConfigurationService.getConfigurations(selectedParticipant?.participantId, {
      signal: configurationAbortController.current?.signal,
    })
      .then((result) => {
        if (thisPromise === configurationPromise.current) {
          if (!result || !result.data) {
            throw new Error("");
          }

          setError(null);
          dispatch(
            setSelectedPPTConfiguration({
              isInitialized: true,
              ...result.data,
              minDate: result.data.minDate ? parseWallClockDate(result.data.minDate).valueOf() : null,
              maxDate: result.data.maxDate ? parseWallClockDate(result.data.maxDate).valueOf() : null,
            }),
          );
        }
      })
      .catch(() => {
        setError("Failed to load configuration data. Please try again later");
      })
      .finally(() => {
        if (thisPromise === configurationPromise.current) {
          setIsLoading(false);
        }
      });

    configurationPromise.current = thisPromise;

    return () => {
      configurationAbortController.current?.abort();
      configurationAbortController.current = new AbortController();
      dispatch(setSelectedPPTConfiguration(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedParticipant) {
      navigate("/");
    }
  }, [selectedParticipant, navigate]);

  return !selectedParticipant ? null : (
    <Stack alignItems="center" direction="column" flexGrow={1} height="100%" padding={2} width="100%">
      <Card elevation={6} sx={{ position: "relative", width: "100%", height: "100%", display: "grid", gridTemplateRows: "auto 1fr" }}>
        {isLoading || error ? (
          <Box
            position="absolute"
            sx={(theme) => ({
              inset: 0,
              width: "100%",
              height: "100%",
              alignContent: "center",
              textAlign: "center",
              background: theme.palette.background.paper,
              zIndex: theme.zIndex.drawer - 1,
            })}
          >
            {!error && <CircularProgress size={150} />}
            <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              {error ? error : "Loading..."}
            </Typography>
          </Box>
        ) : (
          <>
            <AppBar
              position="static"
              sx={{
                background: (theme) => theme.palette.custom.main,
              }}
            >
              <Tabs
                className="black-shadow"
                indicatorColor="secondary"
                onChange={(_, newValue) => setTabIndex(newValue)}
                textColor="inherit"
                value={tabIndex}
                variant="fullWidth"
                centered
              >
                {Array.isArray(navigationMap[0]) &&
                  (navigationMap[0] as NavigationRoute[]).map((item) => {
                    const { title, Icon } = item;
                    const resolvedTitle = typeof title === "function" ? title({ selectedParticipant }) : title;
                    const IconComponent = Icon || (() => null);

                    return (
                      <Tab
                        key={resolvedTitle}
                        icon={<IconComponent />}
                        iconPosition="start"
                        label={<Typography className="black-shadow">{resolvedTitle}</Typography>}
                        sx={{ minHeight: 0 }}
                      />
                    );
                  })}
              </Tabs>
            </AppBar>
            {tabIndex === 0 && (
              <DashboardPPTProvider>
                <DashboardPPT />
              </DashboardPPTProvider>
            )}
            {tabIndex === 1 && <BudgetData />}
            {tabIndex === 2 && <Providers useSelectedPPTProviders />}
          </>
        )}
      </Card>
    </Stack>
  );
};

export default SelectedParticipantNavigationWrapper(SelectedParticipantNavigation);
